'use client';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  useToast,
  TOAST_CLIENT_ERROR_MESSAGE,
  TOAST_SERVER_ERROR_MESSAGE,
} from '@web/Toast';
import { ScreeningStepHeader } from '../ScreeningStepHeader';
import { useSession, wrapErrorWithResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { useAnalysis } from '../../analysis';
import { usePets } from '../../pets';
import { EmailForm } from '../EmailForm';
import {
  HealthScreeningContextActionName,
  useHealthScreening,
} from '../HealthScreeningContainer/context';
import { INVALID_EMAIL_ADDRESS_MSG } from '../../validation';
import {
  CUSTOMER_EXISTS_ERROR_MSG,
  ONE_SCREENING_PER_EMAIL_ERROR_MSG,
} from './constants';
import { petFormToModel } from './functions';
import {
  screeningSubmissionSuccessful,
  screeningSubmissionUnsuccessful,
} from './events';
import { StyledEmailFormContainer } from './styles';

export const EmailFormContainer = ({
  stepTitle,
  stepDescription,
  headerImage,
  form,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [serverError, setServerError] = useState(false);
  const { customer, imageId, pet, dispatch } = useHealthScreening();
  const { service: sessionService } = useSession();
  const { showToast } = useToast();
  const router = useRouter();
  const reporter = useReporter();
  const { updateCustomerWithId, getCustomer, isActive } = useSessionCustomer(
    customer?.smsOptIn,
  );
  const { startWeightAnalysis, error: analysisError } = useAnalysis();
  const { pets, createPet, updatePet } = usePets();

  const handleWeightAnalysis = async (
    { status, data: petData },
    customerData,
  ) => {
    if (status === 201 || status === 200) {
      dispatch({
        action: HealthScreeningContextActionName.setExistingPetId,
        payload: petData?.id,
      });
      const { isBodyCondition } = await startWeightAnalysis(imageId, {
        customer: customerData,
        pet: {
          id: petData?.id,
          name: petData?.name,
        },
        submissionIntent: 'weight_web_acquisition',
      });
      if (isBodyCondition) {
        reporter.tag(screeningSubmissionSuccessful(customerData));
        router.push('/weight/success');
      } else {
        reporter.tag(
          screeningSubmissionUnsuccessful(
            customerData,
            'Unable to assess the photo',
          ),
        );
        showToast({
          status: 'error',
          headline: 'Error',
          message: 'Unable to assess the photo',
        });
        router.push(`/health/weight/photo/?body_condition=${isBodyCondition}`);
      }
    } else {
      reporter.error(analysisError);
      showToast({
        status: 'error',
        headline: 'Error',
        message: TOAST_SERVER_ERROR_MESSAGE,
      });
      setIsSubmitting(false);
    }
  };
  const handlePetSave = async () => {
    let result;
    // in case a pet was created, or session exists with a pet,
    // but there was a network issue or failed prior submission
    const petExists = pets?.find(
      pup => pup?.name === pet.name && !!pup?.weight_photo_id,
    );
    if (petExists) {
      result = await updatePet(petExists?.id, {
        ...petFormToModel(pet),
        weight_photo_id: imageId,
      });
    } else {
      result = await createPet({
        ...petFormToModel(pet),
        weight_photo_id: imageId,
      });
    }
    return result;
  };

  const handleSubmit = async customer => {
    setIsSubmitting(true);
    dispatch({
      type: HealthScreeningContextActionName.setCustomer,
      payload: customer,
    });
    try {
      await sessionService.register('Pup Parent', customer?.email);
      const { status: getStatus, data: customerData } = await getCustomer(
        sessionService?.user?.id,
      );

      if (getStatus === 200) {
        if (!isActive) {
          const { status: updateStatus } = await updateCustomerWithId(
            sessionService?.user?.id,
            {
              first_name: customer?.first_name,
              email: customerData?.email,
              phone: customer?.phoneNumber,
              is_sms: customer?.smsOptIn,
            },
          );

          if (updateStatus === 200) {
            const petResponse = await handlePetSave();
            await handleWeightAnalysis(petResponse, customerData);
          }
        } else if (isActive) {
          reporter.breadcrumb(ONE_SCREENING_PER_EMAIL_ERROR_MSG);
          setServerError(ONE_SCREENING_PER_EMAIL_ERROR_MSG);
        }
      }
    } catch (err) {
      const { error, success } = wrapErrorWithResult(err);
      reporter.error(error.original);
      reporter.tag(
        screeningSubmissionUnsuccessful(
          customer,
          error.original?.response?.data,
        ),
      );
      if (!success) {
        if (
          error?.type === 'emailConflict' ||
          error?.type === 'exisitingCustomer'
        ) {
          setServerError(CUSTOMER_EXISTS_ERROR_MSG);
          setShowLoginPrompt(true);
        } else if (error?.type === 'client' || error?.type === 'network') {
          showToast({
            status: 'error',
            headline: 'Error',
            message: TOAST_CLIENT_ERROR_MESSAGE,
          });
        } else if (error?.type === 'invalidEmail') {
          setServerError(INVALID_EMAIL_ADDRESS_MSG);
        } else if (error?.type === 'server') {
          showToast({
            status: 'error',
            headline: 'Error',
            message: TOAST_SERVER_ERROR_MESSAGE,
          });
        }
        setIsSubmitting(false);
      }
    }
  };

  return (
    <StyledEmailFormContainer>
      <ScreeningStepHeader
        title={stepTitle}
        description={stepDescription}
        image={headerImage}
      />
      <EmailForm
        {...form}
        showLoginPrompt={showLoginPrompt}
        serverError={serverError}
        onSubmit={handleSubmit}
        defaultValues={customer}
        isSubmitting={isSubmitting}
      />
    </StyledEmailFormContainer>
  );
};

EmailFormContainer.displayName = 'EmailFormContainer';
EmailFormContainer.propTypes = {
  stepTitle: PropTypes.string,
  stepDescription: PropTypes.string,
  headerImage: PropTypes.object,
  form: PropTypes.shape({
    emailAddressLabel: PropTypes.string.isRequired,
    phoneNumberLabel: PropTypes.string.isRequired,
    smsLabel: PropTypes.string.isRequired,
    smsDisclaimer: PropTypes.string.isRequired,
    termsText: PropTypes.string.isRequired,
    termsUrl: PropTypes.string.isRequired,
    privacyPolicyText: PropTypes.string.isRequired,
    privacyPolicyUrl: PropTypes.string.isRequired,
    submitButtonText: PropTypes.string.isRequired,
    submitButtonVariant: PropTypes.string.isRequired,
    loginPromptUrlText: PropTypes.string.isRequired,
    loginPromptUrl: PropTypes.string.isRequired,
    loginPromptText: PropTypes.string.isRequired,
  }).isRequired,
};
